import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../recording2Updated/recording2.css";
import Plot from "react-plotly.js";
import PwelchGraph from "./PwelchGraph";
import { getPwelchPlot } from "../../actions/graphActions";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Slider, Box, Typography } from "@mui/material";

const HeatMap = ({ id, currentTimeWindow }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const token = useSelector((state) => state.loginData.token);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qeeg, setQeeg] = useState("Pwelch");
  // Colorbar values (what actually affects the heatmap)
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  // Slider values (completely independent)
  const [sliderRange, setSliderRange] = useState([0, 0]);
  const [dataMin, setDataMin] = useState(0);
  const [dataMax, setDataMax] = useState(0);

  // Input field values (completely independent)
  const [inputMinValue, setInputMinValue] = useState(0);
  const [inputMaxValue, setInputMaxValue] = useState(0);
  const popupRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ left: 0, top: 0 });
  const { pwelchData } = useSelector((state) => state.pwelchPlot);

  // Function to format scientific notation values for better readability
  const formatValue = (value) => {
    if (value === 0) return "0";
    if (Math.abs(value) < 1e-10) {
      return value.toExponential(3);
    }
    if (Math.abs(value) < 0.001 || Math.abs(value) > 1000) {
      return value.toExponential(3);
    }
    return value.toFixed(6);
  };

  // Function to calculate min and max values from data
  const calculateDataRange = (data) => {
    if (!data) return { min: 0, max: 0 };

    let allValues = [];

    // Collect all values from both channels
    if (data.updated_Sxx_ch1) {
      allValues = allValues.concat(data.updated_Sxx_ch1.flat());
    }
    if (data.updated_Sxx_ch2) {
      allValues = allValues.concat(data.updated_Sxx_ch2.flat());
    }

    if (allValues.length === 0) return { min: 0, max: 0 };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    return { min, max };
  };

  useEffect(() => {
    const fetchPwelchData = async () => {
      try {
        const pwelch_data = await dispatch(getPwelchPlot(token, id));
        setHeatmapData(pwelch_data?.data);

        // Calculate and set data range
        const range = calculateDataRange(pwelch_data?.data);
        setDataMin(range.min);
        setDataMax(range.max);
        setSliderRange([range.min, range.max]);
        // Initialize input fields with data range
        setInputMinValue(range.min);
        setInputMaxValue(range.max);

        setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error("Error fetching Pwelch data", {
          autoClose: 3000,
          position: "top-right",
        });
      }
    };

    fetchPwelchData();
  }, [dispatch, token, id]);

  // Function to filter data based on current time window
  const filterDataByTimeWindow = (timeData, zData, timeWindow) => {
    if (!timeData || !zData || !timeWindow)
      return { filteredTime: [], filteredZ: [] };

    const { min, max } = timeWindow;
    const filteredTime = [];
    const filteredZ = [];

    // Find the closest time points to avoid gaps
    let startIndex = 0;
    let endIndex = timeData.length - 1;

    // Find the closest time point to min (not exact match)
    for (let i = 0; i < timeData.length; i++) {
      if (timeData[i] >= min) {
        startIndex = i;
        break;
      }
    }

    // Find the closest time point to max (not exact match)
    for (let i = timeData.length - 1; i >= 0; i--) {
      if (timeData[i] <= max) {
        endIndex = i;
        break;
      }
    }

    // Extract filtered time points
    for (let i = startIndex; i <= endIndex; i++) {
      filteredTime.push(timeData[i]);
    }

    // For heatmap, zData is 2D: rows = frequency bins, columns = time points
    // We need to slice each frequency bin to only include the time window
    if (Array.isArray(zData) && zData.length > 0) {
      // Map over each frequency bin and slice the time dimension
      filteredZ.push(
        ...zData.map((frequencyBin) => {
          if (Array.isArray(frequencyBin)) {
            // Slice the time range for this frequency bin
            return frequencyBin.slice(startIndex, endIndex + 1);
          }
          return frequencyBin;
        })
      );
    }

    return { filteredTime, filteredZ };
  };

  // Define data for each channel with time window filtering
  const channels = [
    {
      x: currentTimeWindow
        ? filterDataByTimeWindow(
            heatmapData?.time_ch1,
            heatmapData?.updated_Sxx_ch1,
            currentTimeWindow
          ).filteredTime
        : heatmapData?.time_ch1,
      // y: heatmapData?.f_pwelch_ch1,
      z: currentTimeWindow
        ? filterDataByTimeWindow(
            heatmapData?.time_ch1,
            heatmapData?.updated_Sxx_ch1,
            currentTimeWindow
          ).filteredZ
        : heatmapData?.updated_Sxx_ch1,
    },
    {
      x: currentTimeWindow
        ? filterDataByTimeWindow(
            heatmapData?.time_ch2,
            heatmapData?.updated_Sxx_ch2,
            currentTimeWindow
          ).filteredTime
        : heatmapData?.time_ch2,
      // y: heatmapData?.f_pwelch_ch2,
      z: currentTimeWindow
        ? filterDataByTimeWindow(
            heatmapData?.time_ch2,
            heatmapData?.updated_Sxx_ch2,
            currentTimeWindow
          ).filteredZ
        : heatmapData?.updated_Sxx_ch2,
    },
  ];

  // Handler for slider change (completely independent - only affects colorbar)
  const handleSliderChange = (event, newValue) => {
    setSliderRange(newValue);
    // Apply colorbar immediately while dragging
    setMinValue(newValue[0]);
    setMaxValue(newValue[1]);
  };

  // Formik with minimal validation (numbers > 0)
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      min: inputMinValue,
      max: inputMaxValue,
    },
    validationSchema: Yup.object({
      min: Yup.number()
        .typeError("Min must be a number")
        .min(0, "Min must be >= 0"),
      max: Yup.number()
        .typeError("Max must be a number")
        .min(0, "Max must be >= 0"),
    }),
    onSubmit: (values) => {
      setMinValue(parseFloat(values.min));
      setMaxValue(parseFloat(values.max));
      setShowPopup(false);
    },
  });
  const handleAdjustClick = (event) => {
    const rect = event.target.getBoundingClientRect();
    setPopupPosition({
      left: rect.right + 10,
      top: rect.top + window.scrollY,
    });
    // Initialize input fields with current colorbar values as plain text
    setInputMinValue(String(minValue));
    setInputMaxValue(String(maxValue));
    // Default slider should span full data range
    setSliderRange([dataMin, dataMax]);
    setShowPopup(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };
    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  return (
    <>
      <div className="fullscreen-container">
        <div className="header-stats">
          <div className="header-stat">
            <p>QEEG: </p>
            <div className="dropdown">
              <button
                className="btn green-btn2 btn-sm dropdown-toggle"
                type="button"
                id="dropdownMenu2"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                defaultValue="Spectrogram"
              >
                {qeeg}
              </button>
              <div className="dropdown-menu" aria-labelledby="dropdownMenu2">
                <button
                  className="dropdown-item"
                  type="button"
                  onClick={() => setQeeg("Spectrogram")}
                >
                  Spectrogram
                </button>
                <button
                  className="dropdown-item"
                  type="button"
                  onClick={() => setQeeg("Pwelch")}
                >
                  Pwelch
                </button>
              </div>
            </div>
          </div>

          <div className="header-stat">
            <p>Output: </p>
            <div className="dropdown">
              <button
                className="btn green-btn2 btn-sm dropdown-toggle"
                type="button"
                id="dropdownMenu2"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                defaultValue="CH1"
              >
                {"CH1"}
              </button>
              <div className="dropdown-menu" aria-labelledby="dropdownMenu2">
                <button
                  className="dropdown-item"
                  type="button"
                  // onClick={() => setLffValue("N/A")}
                >
                  CH2
                </button>
                <button
                  className="dropdown-item"
                  type="button"
                  // onClick={() => setLffValue("1Hz")}
                >
                  Both
                </button>
              </div>
            </div>
            {qeeg === "Spectrogram" && (
              <div className="header-stat" style={{ position: "relative" }}>
                <p style={{ marginLeft: "20px" }}>
                  Colorbar:{" "}
                  <span
                    style={{ marginLeft: "2px", cursor: "pointer" }}
                    onClick={handleAdjustClick}
                    className="colorbar"
                  >
                    Adjust
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
        {qeeg === "Spectrogram" ? (
          pwelchData?.data ? (
            channels.map((channel, index) => (
              <Plot
                key={index}
                data={[
                  {
                    x: channel.x,
                    // y: channel.y,
                    z: channel.z,
                    type: "heatmap",
                    colorscale: "Jet",
                    zmin: minValue,
                    zmax: maxValue,
                    colorbar: { title: "Heatmap Values" },
                  },
                ]}
                layout={{
                  autosize: true,
                  xaxis: {
                    title: {
                      text: "Time (s)",
                      font: { size: 16 },
                    },
                    tickfont: { size: 12 },
                    // Set x-axis range to show only the current 10-second window
                    range: currentTimeWindow
                      ? [currentTimeWindow.min, currentTimeWindow.max]
                      : undefined,
                  },
                  yaxis: {
                    title: {
                      text: "Frequency (Hz)",
                      font: { size: 16 },
                    },
                    tickfont: { size: 12 },
                  },
                  margin: { t: 20, b: 40, l: 50, r: 20 },
                }}
                useResizeHandler
                style={{ width: "100%", height: "200px" }}
              />
            ))
          ) : (
            <div className="text-center">Loading heatmap data...</div>
          )
        ) : (
          <PwelchGraph pwelchData={heatmapData ?? []} />
        )}
        {/* Pop-up for Colorbar Adjustment */}
        {showPopup && (
          <div
            className="popup popup-with-slider"
            ref={popupRef}
            style={{ left: popupPosition.left, top: popupPosition.top }}
          >
            <form onSubmit={formik.handleSubmit} noValidate>
              <div
                style={{ display: "block", gap: "10px", marginBottom: "10px" }}
              >
                <div style={{ flex: 1 }}>
                  <label>
                    <p className="min-label">Min:</p>
                    <div className="input-err">
                      <input
                        name="min"
                        type="text"
                        onChange={(e) => {
                          setInputMinValue(e.target.value);
                          formik.setFieldValue("min", e.target.value);
                        }}
                        onBlur={formik.handleBlur}
                        value={inputMinValue}
                      />
                      {formik.touched.min && formik.errors.min ? (
                        <div className="error">{formik.errors.min}</div>
                      ) : null}
                    </div>
                  </label>
                </div>
                <div>
                  <label>
                    Max:
                    <div className="input-err">
                      <input
                        name="max"
                        type="text"
                        onChange={(e) => {
                          setInputMaxValue(e.target.value);
                          formik.setFieldValue("max", e.target.value);
                        }}
                        onBlur={formik.handleBlur}
                        value={inputMaxValue}
                      />
                      {formik.touched.max && formik.errors.max ? (
                        <div className="error">{formik.errors.max}</div>
                      ) : null}
                    </div>
                  </label>
                </div>
              </div>
              <Box sx={{ width: "100%", mb: 2 }}>
                <p className="min-label">Colorbar Range:</p>
                <Slider
                  value={sliderRange}
                  onChange={handleSliderChange}
                  valueLabelDisplay="off"
                  min={dataMin}
                  max={dataMax}
                  step={Math.abs(dataMax - dataMin) / 1000 || 1}
                  sx={{
                    color: "#092540",
                    "& .MuiSlider-thumb": {
                      backgroundColor: "#092540",
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: "#092540",
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", color: "#666" }}
                >
                  Min: {formatValue(sliderRange[0])} | Max:{" "}
                  {formatValue(sliderRange[1])}
                </Typography>
              </Box>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  type="button"
                  className="btn-set"
                  style={{
                    backgroundColor: "#666",
                    fontSize: "0.8rem",
                    padding: "8px 12px",
                  }}
                  onClick={() => {
                    // Reset inputs to 0 and apply immediately to colorbar
                    setMinValue(0);
                    setMaxValue(0);
                    setInputMinValue("0");
                    setInputMaxValue("0");
                    formik.setFieldValue("min", "0");
                    formik.setFieldValue("max", "0");
                    setShowPopup(false);
                  }}
                >
                  RESET
                </button>
                <button
                  type="submit"
                  className="btn-set"
                  style={{
                    backgroundColor: "#092540",
                  }}
                >
                  SET
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default HeatMap;
